import React, { useState, useEffect, FC, useMemo, useCallback, useRef } from "react";
import { Badge, Row, Col, Button, Card, Alert, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Timeline,
  Collapse as AntCollapse,
  Descriptions,
  Flex,
  Spin,
  Empty,
  Popover,
  Modal as AntModal,
  Form,
  Input,
} from "antd";
import { useNavigate, useParams, useLocation } from "react-router";
import { useSelector } from "react-redux";
import returnsApis from "../../api/returns/returns";
import Swal from "sweetalert2";
import ReturnsAside from "./ReturnsAside";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { RootState } from "../../store/store";
import SignatureModal from "../purchase-orders/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";
import PurchaseReturnPDF from "./PurchaseReturnPDF"
import deliveryAddressApis from "../../api/master/deliveryAddress";

const { Panel } = AntCollapse;

const ViewReturnDetails: FC = () => {
  const { returnNumber }: any = useParams();
  const location = useLocation();
  const pdfBlobData: any = useRef(null);
  const previousPath = (location.state as any)?.previousPath || "/view-returns";
  const extra = {
    hideAside: true,
    reduceTableWidth: 130,
  };
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState<any>({});
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [signature, setSignature] = useState({
    isActive: false,
    label: "Authorised Signatory",
    image: null as string | null,
  });

  const [showSignatureModel, setShowSignatureModel] = useState(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);
  const [rejectRecord, setRejectRecord] = useState<any>(null);

  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [approveRecord, setApproveRecord] = useState<any>(null);
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);

  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
  const [approverIds, setApproverIds] = useState<any>([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);

  const [rejectForm] = Form.useForm();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );

  const canTakeAction = useMemo(() => {
    if (!returnData || returnData.approvalStatus !== "Pending") {
      return false;
    }

    const userIdCandidates = [
      currentUser?.id,
      currentUser?.userId,
      currentUser?.userDetailsId,
    ].filter((value) => value !== undefined && value !== null);

    const normalizedUserId =
      userIdCandidates.length > 0 ? Number(userIdCandidates[0]) : null;

    const roleValues: string[] = [];
    const genericRoleRaw = currentUser?.genericRole;
    if (typeof genericRoleRaw === "string") {
      const genericRole = genericRoleRaw.toLowerCase().trim();
      if (genericRole.length > 0) {
        roleValues.push(genericRole);
      }
    }
    const orgRoleRaw = currentUser?.orgRole;
    if (typeof orgRoleRaw === "string") {
      const orgRole = orgRoleRaw.toLowerCase().trim();
      if (orgRole.length > 0) {
        roleValues.push(orgRole);
      }
    }

    const isAdminRole = roleValues.some((role) =>
      ["admin", "super admin", "superadmin"].includes(role)
    );

    if (isAdminRole) {
      return true;
    }

    const returnsAccess =
      accessibility?.["Returns"] ??
      accessibility?.["Return"] ??
      accessibility?.["Purchase Return"] ??
      accessibility?.["Purchase Returns"];

    const hasApprovePermission =
      returnsAccess === undefined || returnsAccess === null
        ? true
        : Boolean(
          returnsAccess?.approve ??
          returnsAccess?.approval ??
          returnsAccess?.canApprove ??
          returnsAccess?.canApproveReturn
        );

    if (!hasApprovePermission) {
      return false;
    }

    if (
      !Array.isArray(returnData?.returnApprovals) ||
      normalizedUserId === null ||
      Number.isNaN(normalizedUserId)
    ) {
      return false;
    }

    return returnData.returnApprovals.some((approval: any) => {
      const statusValue = (approval?.status ?? "")
        .toString()
        .toLowerCase()
        .trim();

      const pendingStatuses = [
        "",
        "pending",
        "pending approval",
        "pendingapproval",
        "awaiting approval",
        "awaiting",
        "in progress",
        "inprogress",
        "initiated",
        "submitted",
      ];

      const isPending = !statusValue || pendingStatuses.includes(statusValue);

      return isPending && Number(approval?.userId) === normalizedUserId;
    });
  }, [returnData, currentUser, accessibility]);


  const fetchDeliveryAddress = async () => {
    try {
      const { data, status } = await deliveryAddressApis.fetchDeliveryAddress();
      if (status) {
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
    };
  }, []);

  useEffect(() => {
    fetchDeliveryAddress();
    if (returnNumber) {
      fetchReturnDetails();
    }
  }, [returnNumber]);

  const fetchReturnDetails = async () => {
    try {
      setIsLoading(true);
      const res = await returnsApis.fetchReturn(returnNumber);
      const { data, status, message } = res;
      if (status) {
        const isStrict = data?.isStrictApproval === true || data?.isStrictApproval === 1;
        setIsStrictMode(isStrict);

        if (data?.returnApprovals) {
          const processedApprovals = data.returnApprovals.map((approval: any) => ({
            ...approval,
            level: approval.level?.toLowerCase() || approval.level,
            status: approval.status?.toLowerCase() || approval.status
          }));
          data.returnApprovals = processedApprovals;

          // Filter pending approvals
          let ids = processedApprovals
            ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
            ?.map((item: any) => item?.userId);
          setApproverIds(ids);
        }
        setReturnData(data);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.error("Error fetching return details:", error);
      Swal.fire("Error", "Failed to load return details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    // Handle various level formats: level1, Level2, L1, L2, etc.
    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = returnData?.returnApprovals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = returnData?.returnApprovals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUser?.id
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUser?.id, returnData?.returnApprovals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = returnData?.returnApprovals || [];
    const pendingApprovals = approvals
      .filter((approval: any) => approval.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => {
        const aNum = parseLevelNum(a.level);
        const bNum = parseLevelNum(b.level);
        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return aNum - bNum;
      });

    if (pendingApprovals?.length > 0) {
      if (!isStrictMode) {
        // Flexible mode - any approver can approve
        const approverNames = pendingApprovals.map((approval: any) => approval.userName).join(", ");
        return `Status is pending. Any of these approvers can approve: ${approverNames}`;
      } else {
        // Strict mode - must follow sequence
        const nextApprover = pendingApprovals[0];
        const lvl = parseLevelNum(nextApprover.level);
        const levelText = !isNaN(lvl) ? `Level ${lvl}` : nextApprover.roleName || "";
        return `Status is pending. Waiting for ${nextApprover.userName} (${levelText}) to approve first.`;
      }
    }

    return "Status is pending by other approvers";
  };

  const getQuantityText = (value: any, uom?: string) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      isNaN(Number(value))
    ) {
      return "-";
    }
    const formattedQty = Number(value).toFixed(2);
    return `${formattedQty}${uom ? ` ${uom}` : ""}`;
  };

  const dmsList = Array.isArray(returnData?.dms) ? returnData.dms : [];
  const attachmentDocs = dmsList.filter((doc: any) => {
    const type = doc?.moduleFileType?.toLowerCase?.();
    return (
      type === "attachment" ||
      type === "return attachment" ||
      type === "returnattachment"
    );
  });

  const signatureDoc = dmsList.find((doc: any) => {
    const type = doc?.moduleFileType?.toLowerCase?.();
    return type === "signature";
  });

  const returnLinesColumns: any = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text: any, record: any, index: number) => index + 1,
    },
    {
      title: "Material",
      dataIndex: "materialName",
      key: "materialName",
      width: 250,
      fixed: "left",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 200,
    },
    {
      title: "Qty Ordered",
      dataIndex: "orderedQuantity",
      key: "orderedQuantity",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          {getQuantityText(text, record.uom)}
        </div>
      ),
    },
    {
      title: "Invoiced Qty",
      dataIndex: "invoicedQuantity",
      key: "invoicedQuantity",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          {getQuantityText(text, record.uom)}
        </div>
      ),
    },
    {
      title: "Received Qty",
      dataIndex: "receivedQuantity",
      key: "receivedQuantity",
      width: 150,
      render: (_: any, record: any) => {
        const receivedValue = record?.deliveredQuantity;
        return (
          <div style={{ textAlign: "right" }}>
            {getQuantityText(receivedValue, record.uom)}
          </div>
        );
      },
    },
    {
      title: "Return Quantity",
      dataIndex: "returnQuantity",
      key: "returnQuantity",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          {Number(text).toFixed(2)} {record.uom}
        </div>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 150,
      render: (text: any) => (
        <div style={{ textAlign: "right" }}>
          {formatCurrency(text, returnData.currencyCode)}
        </div>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "basicAmount",
      key: "basicAmount",
      width: 150,
      render: (text: any) => (
        <div style={{ textAlign: "right" }}>
          {formatCurrency(text, returnData.currencyCode)}
        </div>
      ),
    },
    {
      title: "CGST",
      children: [
        {
          title: "%",
          dataIndex: "cgstPercentage",
          key: "cgstPercentage",
          width: 90,
          render: (text: any, record: any) =>
            Number(record?.cgst) && Number(record?.sgst)
              ? `${Number(text || 0)}%`
              : "N/A",
        },
        {
          title: "Amount",
          dataIndex: "cgst",
          key: "cgst",
          width: 120,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              {Number(record?.cgst) && Number(record?.sgst)
                ? formatCurrency(text, returnData.currencyCode)
                : "N/A"}
            </div>
          ),
        },
      ],
    },
    {
      title: "SGST",
      children: [
        {
          title: "%",
          dataIndex: "sgstPercentage",
          key: "sgstPercentage",
          width: 90,
          render: (text: any, record: any) =>
            Number(record?.cgst) && Number(record?.sgst)
              ? `${Number(text || 0)}%`
              : "N/A",
        },
        {
          title: "Amount",
          dataIndex: "sgst",
          key: "sgst",
          width: 120,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              {Number(record?.cgst) && Number(record?.sgst)
                ? formatCurrency(text, returnData.currencyCode)
                : "N/A"}
            </div>
          ),
        },
      ],
    },
    {
      title: "IGST",
      children: [
        {
          title: "%",
          dataIndex: "igstPercentage",
          key: "igstPercentage",
          width: 90,
          render: (text: any, record: any) =>
            !Number(record?.cgst) && !Number(record?.sgst)
              ? `${Number(text || 0)}%`
              : "N/A",
        },
        {
          title: "Amount",
          dataIndex: "igst",
          key: "igst",
          width: 120,
          render: (text: any, record: any) => (
            <div style={{ textAlign: "right" }}>
              {!Number(record?.cgst) && !Number(record?.sgst)
                ? formatCurrency(text, returnData.currencyCode)
                : "N/A"}
            </div>
          ),
        },
      ],
    },
    {
      title: "Gross Total",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: 150,
      render: (text: any) => (
        <div style={{ textAlign: "right" }}>
          {formatCurrency(text, returnData.currencyCode)}
        </div>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
    },
  ];

  const getStatusColor = (status: string) => {
    const statusColors: any = {
      Pending: "warning",
      Approved: "success",
      Rejected: "danger",
      "Auto Approved": "success",
    };
    return statusColors[status] || "secondary";
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = returnData?.returnApprovals;

    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Popover
          content={
            <Timeline className="approval-timeline">
              {approvalsData?.map((approval: any, index: number) => {
                const isApproved = approval.status?.toLowerCase() == "approved";
                const isRejected = approval.status?.toLowerCase() == "rejected";
                return (
                  <Timeline.Item
                    key={index}
                    dot={
                      isApproved ? (
                        <CheckCircleOutlined style={{ color: "green" }} />
                      ) : isRejected ? (
                        <CloseCircleOutlined style={{ color: "red" }} />
                      ) : undefined
                    }
                  >
                    {approval?.userName}
                    <div className="designation">{approval?.roleName}</div>
                    {approval?.actionDate && (
                      <div className="designation">
                        {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                      </div>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          }
          title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer"
            }}>
            <span
              style={{
                color: "#2263b3"
              }}
            >
              <Badge color="outline-success">Yes</Badge>
            </span>
            {isStrictMode && (
              <Badge
                color="warning"
                style={{
                  fontSize: "11px",
                  cursor: "pointer",
                  marginLeft: "4px"
                }}
                title="Sequential approvals required, Approvals must follow the sequence"
              >
                Strict
              </Badge>
            )}
          </div>
        </Popover>
      </div>
    );
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const openApproveConfirmSwal = () => {
    if (!signature.image) return;

    setIsModalApproveVisible(false);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = dataURLToFile(signature.image as string, "signature.png");
        await handleApprove(approveRecord, [file]);
      } else {
        setIsModalApproveVisible(true);
      }
    });
  };

  const openRejectConfirmSwal = async () => {
    try {
      await rejectForm.validateFields();

      if (!signature.image) {
        Swal.fire({
          icon: "info",
          title: "Signature Required",
          text: "Please add your signature before rejecting.",
        });
        return;
      }

      setIsModalRejectedVisible(false);

      Swal.fire({
        title: "Confirm Rejection?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reject it!",
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const values = rejectForm.getFieldsValue();
          await apiCallToRejectReturn(rejectRecord, values.reason);
        } else {
          setIsModalRejectedVisible(true);
        }
      });
    } catch (e) {
      console.log("Validation failed:", e);
    }
  };


  const handleApprove = async (returnNumber: number, files: File[] = []) => {
    try {
      const res = await returnsApis.approveReturn(returnNumber, files);
      const { status, message } = res;
      if (status) {
        Swal.fire("Approved!", "Return has been Approved", "success");
        await fetchReturnDetails();
      } else {
        Swal.fire("Failed!", message, "error");
      }
    } catch (error) {
      Swal.fire("Failed!", "Something went wrong", "error");
      setSignature({
        isActive: false,
        label: "Authorised Signatory",
        image: null,
      });
    }
  };

  // Update the reject API call to use signature
  const apiCallToRejectReturn = async (returnNumber: any, reason: string) => {
    try {
      const files: File[] = [];
      if (signature.image) {
        files.push(dataURLToFile(signature.image as string, "signature.png"));
      }

      const res = await returnsApis.rejectReturn(returnNumber, reason, files);
      const { status, message } = res;
      if (status) {
        Swal.fire("Rejected!", "Return has been Rejected", "success");
        setSignature({
          isActive: false,
          label: "Authorised Signatory",
          image: null,
        });
        rejectForm.resetFields();
        navigate(-1);
      } else {
        Swal.fire("Failed!", message, "error");
        setSignature({
          isActive: false,
          label: "Authorised Signatory",
          image: null,
        });
        rejectForm.resetFields();
      }
    } catch (error) {
      Swal.fire("Failed!", "Something went wrong", "error");
      setSignature({
        isActive: false,
        label: "Authorised Signatory",
        image: null,
      });
    }
  };

  const handleCancel = () => {
    setIsModalApproveVisible(false);
    setIsModalRejectedVisible(false);
    rejectForm.resetFields();
  };

  const updatePdfBlobDataRef = (newData: any) => {
    try {
      pdfBlobData.current = newData;
    } catch (error) { }
  };

  const returnBillingDetails = () => {
    const org = returnData?.orgDetails || {};

    if (!returnData || !org) return {};

    const address = deliveryAddressList?.find(
      (item: any) => item?.id == returnData?.billingAddressId
    );

    return {
      name: returnData?.orgName || "",
      addressLine1: address?.addressLine1 || "",
      addressLine2: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.stateName || "",
      pincode: address?.pincode || "",
      country: address?.countryName || "",
      gstin: org?.gstin ?? "",
      pan: org?.pan ?? "",
      cin: org?.cin ?? "",
      phoneNumber: org?.phoneNumber ?? "",
    };
  };

  const returnSupplierDetails = () => {
    // Supplier details from returnData
    const supplier = returnData?.supplierDetails || {};
    const address = supplier?.address || {};
    return {
      name: returnData?.supplierName || "",
      // Address
      addressLine1: address?.addressLine1 || "",
      addressLine2: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.state || "",
      pincode: address?.pincode || "",
      country: address?.country || "",

      // Contact
      phoneNumber: supplier?.phoneNumber || supplier?.contactNumber || "",

      gstin: supplier?.gstin || "",
      pan: supplier?.pan || "",
      cin: supplier?.cin || "",
    };
  };

  // Determine if IGST is used (based on tax configuration)
  const hasIGST = useMemo(() => {
    // Check if any line item has IGST amount > 0
    return returnData?.returnLines?.some((line: any) => Number(line.igstAmount) > 0) || false;
  }, [returnData?.returnLines]);

  return (
    <>
      <Head title="Purchase Return Details" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            {!extra?.hideAside && (
              <div
                className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${sm ? "content-active" : ""
                  }`}
              >
                <ReturnsAside updateSm={setSm} sm={sm} />
              </div>
            )}
            <div className="card-inner">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              {isLoading ? (
                <Flex align="center" gap="middle" className="pageLoader">
                  <Spin size="large" className="m-auto" />
                </Flex>
              ) : (
                <>
                  <BlockHead size="lg">
                    <BlockBetween>
                      <BlockHeadContent>
                        <BlockTitle tag="h4">
                          <span>
                            Order #{returnData?.orderId} | Invoice #
                            {returnData?.invoiceId} | Return #
                            {returnData?.returnId}
                          </span>
                        </BlockTitle>
                      </BlockHeadContent>
                      <div className="d-flex">
                        <div className="paddingLeft10">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() =>
                              navigate(previousPath || "/view-returns")
                            }
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </div>

                        {/*  PDF BUTTON */}
                        <div className="paddingLeft10">
                          {returnData?.orgDetails && deliveryAddressList?.length > 0 && (
                            <PurchaseReturnPDF
                              buyerDetails={returnBillingDetails()}
                              supplierDetails={returnSupplierDetails()}
                              returnData={returnData}
                              buyerOrgLogo={currentUser?.orgLogo}
                              hasIGST={hasIGST}
                              TermsAndConditions={returnData?.termsAndConditions || ""}
                              getPdfUrl={updatePdfBlobDataRef}
                              returnApprovals={returnData?.returnApprovals || []}
                            />
                          )}
                        </div>

                        {returnData?.approvalStatus?.toLowerCase() === "pending" && canTakeAction &&
                          !hasUserAlreadyApproved && (
                            <ul className="nk-tb-actions ms-2">
                              <li>
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="dropdown-toggle btn btn-icon btn-trigger"
                                  >
                                    <Icon name="more-v"></Icon>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <ul className="link-list-opt no-bdr">
                                      {/* {returnData?.approvalStatus?.toLowerCase() === "pending" && canTakeAction &&
                                    !hasUserAlreadyApproved && ( */}
                                      <>
                                        {canUserApproveReject() ? (
                                          <>
                                            <li
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setApproveRecord(returnData?.returnNumber);
                                                setActiveSignatureContext("APPROVE");
                                                setIsModalApproveVisible(true);
                                                const dropdown = document.querySelector(
                                                  ".dropdown-menu.show"
                                                );
                                                if (dropdown) {
                                                  dropdown.classList.remove("show");
                                                }
                                              }}
                                            >
                                              <a href={`#`}>
                                                <Icon name="check" /> Approve
                                              </a>
                                            </li>
                                            <li
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setRejectRecord(returnData?.returnNumber);
                                                setActiveSignatureContext("REJECT");
                                                setIsModalRejectedVisible(true);
                                                const dropdown = document.querySelector(
                                                  ".dropdown-menu.show"
                                                );
                                                if (dropdown) {
                                                  dropdown.classList.remove("show");
                                                }
                                              }}
                                            >
                                              <a href={`#`}>
                                                <Icon name="cross" /> Reject
                                              </a>
                                            </li>
                                          </>
                                        ) : (
                                          <>
                                            <li className="disabled" style={{ position: 'relative' }}>
                                              <div
                                                className="d-flex align-items-center justify-content-between w-100"
                                                style={{
                                                  padding: "8px 16px",
                                                  opacity: 0.6,
                                                  cursor: "not-allowed",
                                                }}
                                              >
                                                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                                  <Icon name="check" />
                                                  <span>Approve</span>
                                                </div>
                                                <Popover
                                                  content={
                                                    <div style={{ padding: "8px", maxWidth: "300px" }}>
                                                      <p style={{ margin: 0 }}>{getPendingStatusMessage()}</p>
                                                    </div>
                                                  }
                                                  title="Approval Status"
                                                  trigger="hover"
                                                >
                                                  <InfoCircleOutlined
                                                    style={{
                                                      fontSize: "16px",
                                                      cursor: "pointer",
                                                      color: "#2263b3",
                                                      marginLeft: "8px"
                                                    }}
                                                  />
                                                </Popover>
                                              </div>
                                            </li>
                                            <li className="disabled">
                                              <div
                                                className="d-flex align-items-center"
                                                style={{
                                                  padding: "8px 16px",
                                                  opacity: 0.6,
                                                  cursor: "not-allowed",
                                                  gap: "8px"
                                                }}
                                              >
                                                <Icon name="cross" />
                                                <span>Reject</span>
                                              </div>
                                            </li>
                                          </>
                                        )}
                                      </>
                                      {/* )} */}
                                    </ul>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </li>
                            </ul>
                          )}
                      </div>
                    </BlockBetween>
                  </BlockHead>

                  {returnData.approvalStatus === "Rejected" && (
                    <Alert color="danger" className="mb-3">
                      <strong>Rejection Reason:</strong>{" "}
                      {returnData.approvalRemarks || "No reason provided"}
                    </Alert>
                  )}

                  <div className="content clearfix">
                    <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                      <Panel header="General Information" key="1">
                        <div className="tab-content mt-0">
                          <Col className="justify-start w-100 flex-wrap p-0">
                            <div className="w-50 p-1 pt-0 pb-0 border-end">
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">Return ID</div>
                                <div className="data-value w-60">
                                  <strong>{returnData.returnId}</strong>
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Return Date
                                </div>
                                <div className="data-value w-60">
                                  {dayjs(returnData.returnDate).format(
                                    "DD-MMM-YYYY"
                                  )}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Return Type
                                </div>
                                <div className="data-value w-60">
                                  {returnData.returnType}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">Status</div>
                                <div className="data-value w-60">
                                  <Badge
                                    color={getStatusColor(
                                      returnData.approvalStatus
                                    )}
                                  >
                                    {returnData.approvalStatus}
                                  </Badge>
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Invoice Number
                                </div>
                                <div className="data-value w-60">
                                  {returnData.invoiceId}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Order Number
                                </div>
                                <div className="data-value w-60">
                                  {returnData.orderId}
                                </div>
                              </div>
                            </div>

                            <div className="w-50 paddingLeft10">
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">Supplier</div>
                                <div className="data-value w-60">
                                  {returnData.supplierName}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">Site</div>
                                <div className="data-value w-60">
                                  {returnData.siteName}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Created By
                                </div>
                                <div className="data-value w-60">
                                  {returnData.createdUserName}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">
                                  Created Date
                                </div>
                                <div className="data-value w-60">
                                  {dayjs(returnData.createdDate).format(
                                    "DD-MMM-YYYY"
                                  )}
                                </div>
                              </div>
                              <div className="data-col align-center p-0 pt-1">
                                <div className="data-label w-40">Approvals</div>
                                <div className="data-value w-60">
                                  <ApprovalTimelineContent />
                                </div>
                              </div>
                              {returnData.description && (
                                <div className="data-col align-center p-0 pt-1">
                                  <div className="data-label w-40">
                                    Description
                                  </div>
                                  <div className="data-value w-60">
                                    {returnData.description}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        </div>
                      </Panel>

                      <Panel header="Material Information" key="2">
                        <div className="table-responsive poTable">
                          <div
                            style={{
                              width:
                                screenSize -
                                (extra?.reduceTableWidth || 380) +
                                "px",
                              margin: "0px auto",
                            }}
                          >
                            {returnData.returnLines?.length > 0 ? (
                              <Table
                                className="customTable"
                                columns={returnLinesColumns}
                                dataSource={returnData.returnLines}
                                rowKey="lineNo"
                                pagination={false}
                                scroll={{ x: 1200 }}
                                bordered
                                size="middle"
                              />
                            ) : (
                              <Empty description="No line items found" />
                            )}
                          </div>
                        </div>

                        <Row
                          style={{
                            margin: "20px",
                          }}
                        >
                          <Col className="justify-end">
                            <div className="w-40">
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Taxable Amount</strong>
                                </div>
                                <span className="r_align">
                                  {formatCurrency(
                                    returnData.netAmount,
                                    returnData.currencyCode
                                  )}
                                </span>
                              </div>
                              {Number(returnData.sgstAmount) &&
                                Number(returnData.cgstAmount) ? (
                                <>
                                  <div className="d-flex justify-content-between">
                                    <div className="w-50 justify-end">
                                      <strong>CGST</strong>
                                    </div>
                                    <span className="r_align">
                                      {formatCurrency(
                                        returnData.cgstAmount,
                                        returnData.currencyCode
                                      )}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <div className="w-50 justify-end">
                                      <strong>SGST</strong>
                                    </div>
                                    <span className="r_align">
                                      {formatCurrency(
                                        returnData.sgstAmount,
                                        returnData.currencyCode
                                      )}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <div className="d-flex justify-content-between">
                                  <div className="w-50 justify-end">
                                    <strong>IGST</strong>
                                  </div>
                                  <span className="r_align">
                                    {formatCurrency(
                                      returnData.igstAmount,
                                      returnData.currencyCode
                                    )}
                                  </span>
                                </div>
                              )}
                              <div
                                className="d-flex justify-content-between"
                                style={{ fontSize: "18px", marginTop: "10px" }}
                              >
                                <div className="w-50 justify-end">
                                  <strong>Total</strong>
                                </div>
                                <strong className="r_align">
                                  {formatCurrency(
                                    returnData.grossAmount,
                                    returnData.currencyCode
                                  )}
                                </strong>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        {signatureDoc && (
                          <Row className="m-4 justify-content-end">
                            <div className="signatureBtnWrapper">
                              <div className="d-flex justify-content-between align-items-center">
                                <strong>Signature</strong>
                              </div>
                              <div
                                style={{
                                  minHeight: signatureDoc?.filePath
                                    ? "0px"
                                    : "120px",
                                  display: signatureDoc?.filePath
                                    ? "block"
                                    : "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {signatureDoc?.filePath && (
                                  <>
                                    <div className="signatueImgWrapper border">
                                      <img
                                        src={signatureDoc?.filePath}
                                        alt="Signed"
                                        style={{ width: "160px" }}
                                      />
                                    </div>

                                    <div>
                                      <div className="form-group">
                                        <label
                                          className="form-label"
                                          htmlFor="signatureLabel"
                                        >
                                          Signature Label
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          name="signatureLabel"
                                          disabled
                                          defaultValue={
                                            returnData?.signatureLabel ||
                                            "Authorised Signatory"
                                          }
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </Row>
                        )}
                      </Panel>
                      <Panel header="Other Information" key="3">
                        <Descriptions
                          title=""
                          className="custom-description-title"
                        >
                          <Descriptions.Item label="">
                            <div>
                              <strong className="d-inline-flex align-items-center">
                                Attachments &nbsp; <PaperClipOutlined />
                                {attachmentDocs?.length === 0 && (
                                  <span className="ms-1">: N/A</span>
                                )}
                              </strong>
                              {attachmentDocs?.length > 0 ? (
                                <ul
                                  className="mt-2"
                                  style={{
                                    listStyle: "none",
                                    paddingLeft: 0,
                                  }}
                                >
                                  {attachmentDocs.map(
                                    (doc: any, index: number) => (
                                      <li
                                        key={`${doc?.docId || doc?.fileName || index
                                          }`}
                                        className="d-flex justify-content-between align-items-center mb-2"
                                      >
                                        <span>
                                          <strong>{index + 1}.</strong>&nbsp;
                                          {doc?.fileName || "Attachment"}
                                        </span>
                                        <a
                                          href={doc?.filePath}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary"
                                        >
                                          <DownloadOutlined />
                                        </a>
                                      </li>
                                    )
                                  )}
                                </ul>
                              ) : null}
                            </div>
                          </Descriptions.Item>
                        </Descriptions>
                      </Panel>
                    </AntCollapse>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      </Content>

      <AntModal
        title="Approve Purchase Return"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}

        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>

            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({
                    isActive: true,
                    label: "Authorised Signatory",
                    image: null,
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
              </button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={() =>
                    setSignature({
                      isActive: true,
                      label: "Authorised Signatory",
                      image: null,
                    })
                  }
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </AntModal >

      <AntModal
        title="Reject Purchase Return"
        visible={isModalRejectedVisible}
        onOk={openRejectConfirmSwal}
        onCancel={() => {
          handleCancel();
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={rejectForm} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image && (
            <a
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() =>
                setSignature({ isActive: true, label: "Authorised Signatory", image: null })
              }
            >
            </a>
          )}


          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ width: "180px", height: "auto" }}
                />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  className="me-2"
                  onClick={() =>
                    setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                  }
                >
                  Reset
                </Button>
              </div>

            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsModalRejectedVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </AntModal>

      {showSignatureModel && (
        <SignatureModal
          modal={showSignatureModel}
          toggle={() => {
            setShowSignatureModel(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModel(false);

            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}
    </>
  );
};

export default ViewReturnDetails;
